import React from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// Loading component
const LoadingSpinner: React.FC = () => (
  <div
    className="flex items-center justify-center min-h-screen"
    style={{ backgroundColor: 'var(--bg-primary)' }}
  >
    <div
      className="animate-spin rounded-full h-12 w-12 border-b-2"
      style={{ borderColor: 'var(--accent)' }}
    ></div>
  </div>
);

// Unauthorized component
const UnauthorizedPage: React.FC = () => (
  <div
    className="flex flex-col items-center justify-center min-h-screen p-4"
    style={{ backgroundColor: 'var(--bg-primary)' }}
  >
    <div className="text-center">
      <h1 className="text-4xl font-bold mb-4" style={{ color: '#ef4444' }}>
        403
      </h1>
      <h2
        className="text-2xl font-semibold mb-4"
        style={{ color: 'var(--text-primary)' }}
      >
        Unauthorized
      </h2>
      <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
        You don't have permission to access this resource.
      </p>
      <button
        onClick={() => window.history.back()}
        className="font-bold py-2 px-4 rounded transition-colors"
        style={{
          backgroundColor: 'var(--accent)',
          color: 'var(--bg-primary)',
          border: '1px solid var(--accent)',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.backgroundColor = 'var(--accent-hover)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.backgroundColor = 'var(--accent)';
        }}
      >
        Go Back
      </button>
    </div>
  </div>
);

// Protected Route Props
interface ProtectedRouteProps {
  children: ReactNode;
  fallback?: ReactNode;
}

// Basic Protected Route - requires authentication
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  fallback,
}) => {
  const { isAuthenticated, isLoading, login } = useAuth();
  const location = useLocation();

  // Handle authentication redirect - must be at the top level
  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Check if we're already in the middle of an auth callback
      const isAuthCallback =
        location.search.includes('state=') || location.search.includes('code=');

      if (!isAuthCallback) {
        // Store the intended destination for after login
        sessionStorage.setItem(
          'loginRedirect',
          location.pathname + location.search
        );
        login();
      }
    }
  }, [isLoading, isAuthenticated, login, location.pathname, location.search]);

  if (isLoading) {
    return fallback || <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    // Show loading while redirecting to Keycloak or processing callback
    return fallback || <LoadingSpinner />;
  }

  return <>{children}</>;
};

// Role-based Protected Route
interface RoleProtectedRouteProps extends ProtectedRouteProps {
  requiredRoles: string[];
  requireAllRoles?: boolean; // If true, user must have ALL roles; if false, user needs ANY role
}

export const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  children,
  requiredRoles,
  requireAllRoles = false,
  fallback,
}) => {
  const { isAuthenticated, isLoading, hasAnyRole, hasAllRoles, login } =
    useAuth();
  const location = useLocation();

  // Handle authentication redirect - must be at the top level
  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Store the intended destination for after login
      sessionStorage.setItem(
        'loginRedirect',
        location.pathname + location.search
      );
      login();
    }
  }, [isLoading, isAuthenticated, login, location.pathname, location.search]);

  if (isLoading) {
    return fallback || <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    // Show loading while redirecting to Keycloak
    return fallback || <LoadingSpinner />;
  }

  // Check role requirements
  const hasRequiredRoles = requireAllRoles
    ? hasAllRoles(requiredRoles)
    : hasAnyRole(requiredRoles);

  if (!hasRequiredRoles) {
    return fallback || <UnauthorizedPage />;
  }

  return <>{children}</>;
};

// Admin Route - shorthand for admin role protection
export const AdminRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  return (
    // <RoleProtectedRoute requiredRoles={['admin']} fallback={fallback}>
    <div>{children}</div>
    // </RoleProtectedRoute>
  );
};

// Multiple role route - user needs any of the specified roles
export const MultiRoleRoute: React.FC<{
  children: ReactNode;
  roles: string[];
  fallback?: ReactNode;
}> = ({ children, roles, fallback }) => {
  return (
    <RoleProtectedRoute
      requiredRoles={roles}
      requireAllRoles={false}
      fallback={fallback}
    >
      {children}
    </RoleProtectedRoute>
  );
};

// Higher-order component for conditional rendering based on roles
interface ConditionalRenderProps {
  children: ReactNode;
  roles?: string[];
  requireAllRoles?: boolean;
  fallback?: ReactNode;
  requireAuth?: boolean;
}

export const ConditionalRender: React.FC<ConditionalRenderProps> = ({
  children,
  roles = [],
  requireAllRoles = false,
  fallback = null,
  requireAuth = true,
}) => {
  const { isAuthenticated, hasAnyRole, hasAllRoles } = useAuth();

  // If authentication is required but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    return <>{fallback}</>;
  }

  // If no roles specified, just check authentication
  if (roles.length === 0) {
    return isAuthenticated || !requireAuth ? <>{children}</> : <>{fallback}</>;
  }

  // Check role requirements
  const hasRequiredRoles = requireAllRoles
    ? hasAllRoles(roles)
    : hasAnyRole(roles);

  return hasRequiredRoles ? <>{children}</> : <>{fallback}</>;
};
